import React, { forwardRef } from 'react';
import { clsx } from '../../../utils/clsx/clsx';
import './text.css';
import type { COLOR_NAMES } from '../../../constants/colors';

type Variant =
  | 'h1'
  | 'display' // deprecated, use h1 instead
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subhead' // deprecated, use h6 instead
  | 'body'
  | 'body-lg'
  | 'body-sm'
  | 'caption' // deprecated, use body-sm instead
  | 'button'
  | 'button-sm'
  | 'link'
  | 'link-sm'
  | 'inherit'
  | 'marketing-display'
  | 'marketing-h1'
  | 'marketing-h2'
  | 'marketing-h3'
  | 'marketing-h4'
  | 'marketing-h5'
  | 'marketing-body'
  | 'marketing-sm'
  | 'marketing-xs'
  | 'reading-h1'
  | 'reading-h2'
  | 'reading-h3'
  | 'reading-body'
  | 'reading-body-sm';

type Weight = 'bold' | 'normal' | 'light' | 'semibold';
type Transform = 'capitalize' | 'lowercase' | 'uppercase';
type Align = 'left' | 'center' | 'right';
type ColorName = typeof COLOR_NAMES[number];

interface TextProps extends React.HTMLAttributes<HTMLElement> {
  variant?: Variant;
  component?: React.ElementType;
  weight?: Weight;
  italic?: boolean;
  nowrap?: boolean;
  transform?: Transform;
  align?: Align;
  gutterBottom?: boolean;
  style?: React.CSSProperties;
  className?: string;
  color?: ColorName;
}

const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      variant = 'body',
      component,
      weight,
      italic = false,
      nowrap = false,
      transform,
      align,
      gutterBottom = false,
      style,
      className,
      color = 'dark',
      children,
      ...props
    },
    ref
  ) => {
    const Component = component || 'span';
    return (
      <Component
        ref={ref}
        className={clsx(
          variant !== 'inherit' && `typography-${variant}`,
          italic && 'font-italic',
          weight && `font-weight-${weight}`,
          nowrap && 'text-truncate display-inline-block',
          transform && `text-${transform}`,
          align && `text-${align}`,
          gutterBottom && 'mb-1',
          className
        )}
        style={{ color: color !== 'inherit' ? `var(--${color})` : 'inherit', ...style }}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Text.displayName = 'Text';

export default Text;
